import partition from 'lodash/partition';

import {PRIMITIVE_MATH_TYPES, PropertyType, ResourceType} from 'common/report/constants';
import {cohortsStore} from 'common/report/stores/cohorts';
import {getPropertyTypeForMathType} from 'common/report/util';
import {assert, replaceByIndex} from 'common/util';
import {DEFAULT_FILTER_OPERATOR} from 'common/query-builder/cohorts-query-builder/models/constants';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {DEFAULT_FILTER_VALUE, PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ListOperator, NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {isProperty, MenuItemType, SelectPropertyEvent} from 'common/query-builder/query-block/properties-menu/types';
import {
  Action,
  CohortGroupEntry,
  EntryPart,
  EntrySectionUpdateEvent,
  FiltersOperator,
  QueryBehaviorEntry,
  QueryFilterEntry,
} from 'common/query-builder/query-entry-section/types';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {getCohortFromPropertyItem} from 'common/query-builder/query-block/utils/util';
import {Behavior} from 'common/types/reports/query';
import {populateListPropertyAttrs} from 'common/query-builder/query-entry-section/row-types/row-util';

import {BaseEntryRow} from '../base';
import {
  DEFAULT_EMPTY_BEHAVIORAL_ENTRY,
  isBehavioralFilterNegated,
  transformBehavioralFilterToBehavioralEntry,
} from '../behavior/utils';
import {applyFilterOperatorToFilter, getFilterEntriesFromFilters} from '../filter/utils';
// eslint-disable-next-line import/no-cycle
import {CohortGroupEntryRowEl} from './template';
import {BehavioralAction} from './types';

/** defines what type of behavioral filters operator is selected  */
export type FiltersOperatorSelection = [FiltersOperator, boolean];

export class CohortGroupEntryRow extends BaseEntryRow<CohortGroupEntry> {
  template = CohortGroupEntryRowEl;

  isPlaceholder() {
    return !this.entry.filters.every((filter) => new PropertyFilter(filter).isValid());
  }

  isIntermediatePlaceholder() {
    return this.isPlaceholder() && !!this.entry.event;
  }

  getGroupTitle() {
    const cohort = this.entry.event;
    let title = `All ${this.section.getSelectedGroupLabel(true)}`;

    if (Number.isInteger(cohort.value)) {
      const label = cohortsStore.value[cohort.value as number]?.name || cohort.label;
      title = cohort.negated ? `Not in ${label}` : label;
    }

    return title;
  }

  /** determines if any of the commited entries contain extended logical operators (`and any`, `or all`) */
  showExtendedLogicalOperators(operatorField: `filtersOperator` | `behavioralFiltersOperator`) {
    const entries = this.section.attr(`entries`) as CohortGroupEntry[];
    return entries.some(
      (entry) => entry[operatorField] === FiltersOperator.AndAny || entry[operatorField] === FiltersOperator.OrAll,
    );
  }

  handleAbortStage() {
    if (this.entry.filters.length === 0) {
      this.section.updateEntries(Action.Remove, {index: this.index});
    }
  }

  handleEntryGroupingOperatorClick() {
    const entry = {
      ...this.entry,
      groupingOperator: this.entry.groupingOperator === FiltersOperator.Or ? FiltersOperator.And : FiltersOperator.Or,
    } as CohortGroupEntry;

    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  }

  updateBehavioralFiltersToThen(): PropertyFilter[] {
    // when changing to a funnel behavior, all numeric entryFilters must switch to a
    // `is equal to` numeric frequency filter
    // const behaviorEntry = this.getEntry<QueryBehaviorEntry>(index);
    let [behavioralFrequencyFilters, filters] = partition(
      this.entry.filters,
      (filter) => filter.customProperty && isBehavioralCustomProperty(filter.customProperty),
    );

    behavioralFrequencyFilters = behavioralFrequencyFilters.map((filter) => {
      const filterOperator = [NumberOperator.EqualTo, NumberOperator.AtLeast].includes(
        filter.filterOperator as NumberOperator,
      )
        ? filter.filterOperator
        : NumberOperator.EqualTo;

      return applyFilterOperatorToFilter(
        filterOperator,
        new PropertyFilter({
          ...filter,
          propertyType: PropertyType.Number,
          propertyDefaultType: PropertyType.Number,
          customProperty: {
            name: ``,
            description: ``,
            // resets aggregations to defaults
            behavior: {
              ...filter.customProperty.behavior,
              property: null,
              aggregationOperator: PRIMITIVE_MATH_TYPES.TOTAL,
            },
          },
          filterValue: filter.propertyType !== PropertyType.Number ? DEFAULT_FILTER_VALUE.number : filter.filterValue,
        }),
      ).getAttributes();
    }) as PropertyFilter[];

    return [...filters, ...behavioralFrequencyFilters];
  }

  handleBehavioralFilterOperatorChange({
    operator,
    action,
    entry,
    filterIndex,
  }: {
    operator?: FiltersOperator;
    action?: BehavioralAction;
    entry: QueryBehaviorEntry;
    filterIndex: number;
  }) {
    const behavioralEntry = {
      ...entry,
      behavioralFiltersOperator: operator ?? entry.behavioralFiltersOperator,
    };

    // switching to `then` should update all of the behavioral frequency filters
    if (this.entry.behavioralFiltersOperator !== FiltersOperator.Then && operator === FiltersOperator.Then) {
      this.section.updateEntries(Action.Edit, {
        entry: {
          ...this.entry,
          behavioralFiltersOperator: FiltersOperator.Then,
          filters: this.updateBehavioralFiltersToThen(),
        },
        index: this.index,
      });
      return;
      // when changing to `who did not, did not, etc...`, we update the frequency filter to `= 0 times`
    } else if (action === BehavioralAction.DidNot) {
      behavioralEntry.property = null;
      behavioralEntry.aggregationOperator = PRIMITIVE_MATH_TYPES.TOTAL;
      behavioralEntry.entryFilter = {
        ...DEFAULT_EMPTY_BEHAVIORAL_ENTRY.entryFilter,
        filterValue: 0,
        filterOperator: NumberOperator.EqualTo,
      };
    } else if (action && isBehavioralFilterNegated(entry)) {
      // when changing to `who did, and did, etc...` on a negated entry, `= 0 times`, we revert back to the default state
      behavioralEntry.entryFilter = {...DEFAULT_EMPTY_BEHAVIORAL_ENTRY.entryFilter};
    }

    const entryFilters = this.section.helpers.getEntryFilters(this.index);
    const entries = (entryFilters[EntryPart.BehavioralFrequencyFilters] ?? []).map((filter) =>
      transformBehavioralFilterToBehavioralEntry(filter),
    );

    this.handleFilterSectionUpdate(EntryPart.BehavioralFrequencyFilters, {
      entries: replaceByIndex(entries, filterIndex, behavioralEntry),
      updatedEntry: behavioralEntry,
      updatedEntryIndex: filterIndex,
    });
  }

  /** handles property item select, either from the main `All Users` block or adding a new filter */
  handlePropertyItemClick(ev: SelectPropertyEvent) {
    const {menuItemType} = ev.detail;
    const entryFilters = this.section.helpers.getEntryFilters(this.index);
    const item = ev.detail.item;

    assert(!Array.isArray(item));
    /**
     * we need to determine what type of entry point the user clicked on
     * for an event, we construct a behavioral frequency filter
     */
    if (menuItemType === MenuItemType.Events) {
      const event = item as EventItem;
      const behavioralEntry = {
        ...DEFAULT_EMPTY_BEHAVIORAL_ENTRY,
        event,
      };

      const entries = [
        ...(entryFilters[EntryPart.BehavioralFrequencyFilters] ?? []).map((filter) =>
          transformBehavioralFilterToBehavioralEntry(filter),
        ),
        behavioralEntry,
      ];

      this.handleFilterSectionUpdate(EntryPart.BehavioralFrequencyFilters, {
        entries,
        updatedEntry: null,
        updatedEntryIndex: null,
      });
    } else if (menuItemType === MenuItemType.Cohorts) {
      // construct a default cohort filter
      const cohortFilter = new PropertyFilter({
        resourceType: ResourceType.Cohort,
        propertyType: PropertyType.List,
        filterOperator: ListOperator.Contains,
        filterValue: [getCohortFromPropertyItem(item)],
      });

      this.handleFilterSectionUpdate(EntryPart.Filters, {
        entries: getFilterEntriesFromFilters(
          [...entryFilters[EntryPart.Filters], cohortFilter],
          this.entry.filtersOperator,
        ),
        updatedEntry: null,
        updatedEntryIndex: null,
      });
    } else {
      // for all other property types we staged a new filter
      const {resourceType, value} = item;

      let customProperty,
        customPropertyId,
        dataGroupId,
        tempDataGroupId,
        joinPropertyType,
        propertyDefaultType,
        type,
        scdEvent;

      if (isProperty(item)) {
        ({
          customProperty,
          customPropertyId,
          dataGroupId,
          tempDataGroupId,
          joinPropertyType,
          scdEvent,
          propertyDefaultType,
          type,
        } = item);
      }
      // not all custom properties have a well-defined type because the formula could return
      // mixed-typed values. Use 'string' for those cases to hydrate the rest of the filter ui
      const {name, propertyType} = customProperty || {name: null, propertyType: PropertyType.String};
      const propertyFilterAttrs = {
        ...(customProperty ? {customProperty} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
        ...(dataGroupId ? {dataGroupId} : {}),
        ...(tempDataGroupId ? {tempDataGroupId} : {}),
        resourceType,
        propertyDefaultType: propertyDefaultType || propertyType,
        propertyName: value || name,
        propertyType: type || propertyType,
        ...(joinPropertyType ? {joinPropertyType} : {}),
        ...(scdEvent ? {scdEvent} : {}),
      };

      const filter = populateListPropertyAttrs(new PropertyFilter(propertyFilterAttrs));

      if (filter.isValid()) {
        this.handleFilterSectionUpdate(EntryPart.Filters, {
          entries: getFilterEntriesFromFilters(
            [...entryFilters[EntryPart.Filters], filter],
            this.entry.filtersOperator,
          ),
          updatedEntry: null,
          updatedEntryIndex: null,
        });
      } else {
        const partialEntry = getFilterEntriesFromFilters([filter], this.entry.filtersOperator)[0];
        this.section.helpers.showPlaceholderForEntry({part: EntryPart.Filters, index: this.index, entry: partialEntry});
      }
    }
  }

  /**
   * Handles filter updates from a nested QueryEntrySection
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleFilterSectionUpdate(
    source: EntryPart,
    {
      entries,
      updatedEntry,
      updatedEntryIndex,
      queryReason,
    }: Partial<EntrySectionUpdateEvent<QueryFilterEntry | QueryBehaviorEntry>['detail']>,
  ) {
    const [behavioralFrequencyFilters, filters] = partition(
      this.entry.filters,
      (filter) => filter.customProperty && isBehavioralCustomProperty(filter.customProperty),
    );

    // update the entry's filters operator to match the selected operator from the incoming
    // updated entry, if it's not available fetch it from the first commited filter
    const filtersOperator = updatedEntry?.filtersOperator ?? entries[0]?.filtersOperator;
    const behavioralFiltersOperator =
      (updatedEntry as QueryBehaviorEntry)?.behavioralFiltersOperator || this.entry?.behavioralFiltersOperator;

    const updatedFilters = {
      [EntryPart.Filters]: filters,
      // Refold behavioral filters
      [EntryPart.BehavioralFrequencyFilters]: behavioralFrequencyFilters,
      [source]: entries.map(
        (entry, entryIndex): Partial<PropertyFilter> => {
          if (source === EntryPart.BehavioralFrequencyFilters) {
            const {type: _, entryFilter, ...behavior} = entry as QueryBehaviorEntry;
            const filterOperator = entryFilter?.filterOperator || NumberOperator.AtLeast;
            const newEntry = entryIndex === updatedEntryIndex ? updatedEntry : entry;

            /**
             * @todo [pablo] computing entryFilter type based on math type should be moved
             * one level down (on filters entry section) since this can emit invalid filters
             * forcing validation to be done in a different layer
             */
            const derivedPropertyType = getPropertyTypeForMathType(
              newEntry.aggregationOperator,
              newEntry.property?.propertyType ?? newEntry.property?.type,
            );
            const filterValue = entryFilter?.filterValue ?? DEFAULT_FILTER_VALUE[derivedPropertyType];

            const filter = new PropertyFilter({
              // Defaults for behavioral frequency entry
              propertyType: derivedPropertyType,
              propertyDefaultType: derivedPropertyType,
              ...entryFilter,
              resourceType: ResourceType.User,
              // reset properties defined in entryFilter
              propertyName: null,
              dataGroupId: null,
              tempDataGroupId: null,
              customPropertyId: null,
              filterValue,
              // @ts-ignore
              filterOperator,
              ...(behavior?.property
                ? {
                    propertyType: derivedPropertyType,
                    propertyDefaultType: derivedPropertyType,
                  }
                : {}),
              ...(entryFilter && derivedPropertyType !== entryFilter.propertyType
                ? {
                    propertyType: derivedPropertyType,
                    propertyDefaultType: derivedPropertyType,
                    filterValue: DEFAULT_FILTER_VALUE[derivedPropertyType],
                    filterOperator:
                      derivedPropertyType === PropertyType.Number
                        ? NumberOperator.AtLeast
                        : DEFAULT_FILTER_OPERATOR[derivedPropertyType],
                  }
                : {}),
              customProperty: {
                name: ``,
                description: ``,
                behavior: behavior as Behavior,
              },
            });

            !filter.isValid() && this.section.setActiveEntry({index: this.index, part: EntryPart.EntryFilterValue});

            return filter.getAttributes();
          }

          return (entry as QueryFilterEntry).event;
        },
      ),
    };

    const entry = {
      ...this.entry,
      filters: [...updatedFilters[EntryPart.Filters], ...updatedFilters[EntryPart.BehavioralFrequencyFilters]],
      ...(source !== EntryPart.BehavioralFrequencyFilters
        ? {
            filtersOperator,
          }
        : {
            filtersOperator: this.entry.filtersOperator || FiltersOperator.And,
            // when removing most behavioral filters, reset the filters operator to `And`
            behavioralFiltersOperator:
              updatedFilters[EntryPart.BehavioralFrequencyFilters].length === 1
                ? FiltersOperator.And
                : behavioralFiltersOperator,
          }),
    };

    // when deleting filters and the behavioral operator is `and_any, or_all`, we transition to and/or respectively
    if (
      behavioralFrequencyFilters.length >= 3 &&
      updatedFilters[EntryPart.BehavioralFrequencyFilters].length < 3 &&
      (this.entry.behavioralFiltersOperator === FiltersOperator.AndAny ||
        this.entry.behavioralFiltersOperator === FiltersOperator.OrAll)
    ) {
      entry.behavioralFiltersOperator =
        this.entry.behavioralFiltersOperator === FiltersOperator.AndAny ? FiltersOperator.And : FiltersOperator.Or;
    }

    // when deleting all filters, delete the whole group
    const shouldDeleteGroup = entry.filters.length === 0 && this.section.state.stagedRows.length > 1;
    this.section.updateEntries(shouldDeleteGroup ? Action.Remove : Action.Edit, {
      entry,
      index: this.index,
      queryReason,
    });
  }
}
